import { PropsWithChildren, useEffect, useRef, useState } from "react";
import Tooltip, { TooltipProps } from "./Tooltip";

interface OverflowTooltipProps extends TooltipProps {
    isOverlapping?: boolean;
    tooltipProps?: { placement?: TooltipProps["placement"]; align: TooltipProps["align"] };
    /**
     * Opt-in edge-aware placement for the standard (non-overlapping) tooltip.
     * The default "bottom" placement centres the popup under the trigger and lets
     * rc-tooltip shift it horizontally to stay on screen — but rc-tooltip 5.x
     * clamps the arrow to the popup, so a trigger near the viewport's right (or
     * left) border ends up with the caret pointing at empty space beside it
     * instead of at the trigger (operator-task-fe-canvas-title-hover-centering-
     * font-size, Margot 2026-09-24). When enabled we flip to bottomRight/bottomLeft
     * near a border so the popup's near edge aligns to the trigger and the caret
     * stays over it, with no overflow shift. Measured per-hover so it stays correct
     * as a zoomable/pannable canvas moves the trigger around the viewport.
     */
    edgeAwarePlacement?: boolean;
}

/**
 * Border-aware placement for a bottom-anchored overflow tooltip. Given the
 * trigger's horizontal centre and the viewport width, return the rc-tooltip
 * placement that keeps the caret pointing at the trigger: near the right border
 * anchor the popup's right edge to the trigger (bottomRight), near the left
 * border anchor its left edge (bottomLeft), otherwise centre it (bottom). This
 * sidesteps rc-tooltip 5.x clamping the arrow when it shifts the popup to avoid
 * viewport overflow (operator-task-fe-canvas-title-hover-centering-font-size).
 */
export const pickEdgePlacement = (
    centerX: number,
    viewportWidth: number,
): "bottom" | "bottomLeft" | "bottomRight" => {
    if (viewportWidth <= 0) {
        return "bottom";
    }
    if (centerX > viewportWidth * 0.7) {
        return "bottomRight";
    }
    if (centerX < viewportWidth * 0.3) {
        return "bottomLeft";
    }
    return "bottom";
};

/**
 * Tooltip shown only when its children are truncated
 * @param  {Node} children component wrapped by Tooltip, could be truncated
 * @param {string | Node} overlay text or component shown in a Tooltip
 * @param {CSSProperties} overlayStyle inner style of a Tooltip Tooltip
 * @param {boolean} isOverlapping shows whether Tooltip should overlap the content
 * @returns children component wrapped in a custom Tooltip
 */
const OverflowTooltip = ({
    children,
    overlay,
    overlayStyle,
    isOverlapping = true,
    tooltipProps,
    overlayClassName,
    // Truncated-cell tooltips are plain overflow labels: while the cursor sits on the
    // popover, the rc-tooltip overlay (rendered in a portal outside the scroll container)
    // swallows wheel events and the underlying table/page stops scrolling. Dismiss the
    // tooltip on wheel by default so the wheel reaches the scroll container again.
    hideOnWheel = true,
    edgeAwarePlacement = false,
}: PropsWithChildren<OverflowTooltipProps>) => {
    const [isTooltipShown, setTooltipShown] = useState(false);
    const [edgePlacement, setEdgePlacement] = useState<TooltipProps["placement"]>();
    const ref = useRef<HTMLDivElement>(null);

    // Choose a border-aware placement from where the trigger currently sits in the
    // viewport, so the caret keeps pointing at the trigger near the left/right edge
    // instead of being clamped beside it. Recomputed on each hover because a
    // zoomable/pannable canvas moves the trigger without a resize/remount.
    const measureEdgePlacement = () => {
        if (!edgeAwarePlacement || isOverlapping || !ref.current) {
            return;
        }
        const rect = ref.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        setEdgePlacement(pickEdgePlacement(rect.left + rect.width / 2, viewportWidth));
    };

    useEffect(() => {
        const resizeObserver = new ResizeObserver(([entry]) => {
            if (
                entry.target.scrollWidth > entry.target.clientWidth ||
                (entry.target.firstElementChild &&
                    entry.target.firstElementChild?.scrollHeight >
                        entry.target.firstElementChild?.clientHeight)
            ) {
                setTooltipShown(true);
            } else {
                setTooltipShown(false);
            }
        });

        if (ref.current) {
            resizeObserver.observe(ref.current);
        }
        const refCurrent = ref.current;
        return () => {
            if (refCurrent) {
                resizeObserver.unobserve(refCurrent);
            }
            setTooltipShown(false);
        };
    }, [overlay]);

    const overlappingProps = isOverlapping
        ? {
              overlayClassName: `light overlapText ${overlayClassName ?? ""}`,
              align: { offset: [-5, -25] },
              placement: "bottomLeft" as "bottomLeft",
          }
        : {
              overlayClassName,
              ...(edgeAwarePlacement && edgePlacement ? { placement: edgePlacement } : {}),
          };

    return (
        <Tooltip
            overlay={overlay}
            isHidden={!isTooltipShown}
            overlayStyle={overlayStyle}
            hideOnWheel={hideOnWheel}
            {...tooltipProps}
            {...overlappingProps}
        >
            <div
                ref={ref}
                onMouseEnter={measureEdgePlacement}
                style={{
                    whiteSpace: "pre",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                }}
            >
                {children}
            </div>
        </Tooltip>
    );
};

export default OverflowTooltip;
